import React, { useRef } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useReactToPrint } from "react-to-print";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Printer, X } from "lucide-react";
import { money, fechaMX } from "@/lib/format";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";

const fecha = (d) => fechaMX(d);

export default function EstadoCuentaResumen({ open, onClose, tipo, items = [] }) {
  const printRef = useRef(null);
  const esVencidas = tipo === "vencidas";
  const titulo = esVencidas ? "Estado de Cuenta - Vencidas" : "Estado de Cuenta - Por Vencer";
  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: titulo.replace(/\s+/g, "_") });

  const total = items.reduce((a, c) => a + Number(c.monto || 0), 0);
  const plural = items.length > 1;
  const encabezado = esVencidas
    ? (plural ? "Facturas Vencidas" : "Factura Vencida")
    : (plural ? "Facturas por Vencer" : "Factura por Vencer");

  const conFecha = items.filter((x) => x.fecha_vencimiento);
  let fechaRef = null, diasRetraso = 0;
  if (conFecha.length) {
    if (esVencidas) {
      const ref = conFecha.reduce((a, b) => (a.dias <= b.dias ? a : b)); // más vencida (dias más negativo)
      fechaRef = ref.fecha_vencimiento; diasRetraso = -ref.dias;
    } else {
      const ref = conFecha.reduce((a, b) => (a.dias <= b.dias ? a : b)); // vence primero
      fechaRef = ref.fecha_vencimiento;
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" data-testid="estado-resumen-dialog">
        <div className="flex justify-end gap-3 p-4 border-b sticky top-0 bg-white z-10 print:hidden">
          <Button onClick={handlePrint} variant="outline" data-testid="estado-resumen-print"><Printer className="mr-2 h-4 w-4" /> Imprimir</Button>
          <Button onClick={onClose} variant="secondary"><X className="mr-2 h-4 w-4" /> Cerrar</Button>
        </div>

        <div ref={printRef} className="bg-white p-8 md:p-10 text-sm" data-testid="estado-resumen-doc">
          <PrintHeader title={encabezado} fecha={fechaMX(new Date())} screen />
          <div className="flex justify-between items-start mb-8 border-b pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{titulo}</h2>
              <p className="mt-1 text-slate-600 text-xs md:text-sm">{esVencidas ? "Facturas con saldo vencido pendientes de pago." : "Facturas próximas a vencer."}</p>
            </div>
            <div className="text-right text-xs md:text-sm">
              <p className="font-semibold text-slate-900 mb-1">Fecha de Emisión</p>
              <p className="text-slate-600">{format(new Date(), "dd 'de' MMMM, yyyy", { locale: es })}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10 items-stretch">
            <Card className="bg-slate-50 border-slate-100 shadow-none"><CardContent className="p-5"><p className="text-slate-500 text-xs font-medium mb-1 uppercase tracking-wider">Facturas</p><p className="text-2xl font-bold text-slate-900">{items.length}</p></CardContent></Card>
            <Card className={`shadow-none ${esVencidas ? "bg-white border-red-200 shadow-md" : "bg-slate-50 border-slate-100"}`}><CardContent className="p-5"><p className={`text-xs font-semibold mb-1 uppercase tracking-wider ${esVencidas ? "text-red-500" : "text-slate-500"}`}>{esVencidas ? "Total Vencido" : "Total por Vencer"}</p><p className={`text-2xl font-bold ${esVencidas ? "text-red-600" : "text-amber-600"}`}>{money(total)}</p></CardContent></Card>
            {esVencidas ? (
              <Card className="bg-white border-red-200 shadow-none" data-testid="edo-fecha-card"><CardContent className="p-5"><p className="text-red-500 text-xs font-semibold mb-1 uppercase tracking-wider">Venció el</p><p className="text-xl font-bold text-slate-900">{fechaRef ? fecha(fechaRef) : "-"}</p>{fechaRef && <p className="text-xs font-semibold text-red-600 mt-1">Llevas {diasRetraso} {diasRetraso === 1 ? "día" : "días"} de retraso</p>}</CardContent></Card>
            ) : (
              <Card className="bg-slate-50 border-slate-100 shadow-none" data-testid="edo-fecha-card"><CardContent className="p-5"><p className="text-slate-500 text-xs font-semibold mb-1 uppercase tracking-wider">Fecha límite de pago</p><p className="text-xl font-bold text-amber-600">{fechaRef ? fecha(fechaRef) : "-"}</p></CardContent></Card>
            )}
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4 text-slate-800 border-b pb-2">Detalle de Facturas</h3>
            {items.length === 0 ? (
              <p className="text-slate-500 italic py-4 text-center bg-slate-50 rounded">Sin facturas.</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-100">
                    <TableRow>
                      <TableHead>Factura</TableHead><TableHead>Pedido</TableHead><TableHead>Cliente</TableHead>
                      <TableHead>Fecha Factura</TableHead><TableHead>Fecha Vencimiento</TableHead>
                      <TableHead className="text-center">{esVencidas ? "Días Vencidos" : "Vence en"}</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell className="font-medium">{f.factura || "-"}</TableCell>
                        <TableCell className="text-slate-600">{f.numero_pedido}</TableCell>
                        <TableCell>{f.cliente}</TableCell>
                        <TableCell>{fecha(f.fecha_facturacion)}</TableCell>
                        <TableCell>{fecha(f.fecha_vencimiento)}</TableCell>
                        <TableCell className="text-center">
                          {esVencidas
                            ? <span className="text-red-600 font-bold">{-f.dias} días</span>
                            : <span className="text-amber-600 font-medium">{f.dias === 0 ? "hoy" : `${f.dias} días`}</span>}
                        </TableCell>
                        <TableCell className="text-right font-bold text-slate-800">{money(f.monto)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter className="bg-slate-50 font-bold text-slate-900">
                    <TableRow><TableCell colSpan={6} className="text-right">{esVencidas ? "Total Vencido:" : "Total por Vencer:"}</TableCell><TableCell className="text-right">{money(total)}</TableCell></TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </div>
          <PrintFooter screen />
        </div>
      </DialogContent>
    </Dialog>
  );
}
